(function () {
	/* ═══════════════════════════════════════════════════════════════════════
	   WHOLESALE PRICING SYSTEM
	   ─────────────────────────────────────────────────────────────────────
	   Reads `.wholesale-config` JSON blocks rendered by main-product.liquid.
	   • Wholesaler  → auto-selects the "Wholesale" pricing variant, hides
	                   the Pricing picker fieldset, shows a badge.
	   • Retail user → hides the "Wholesale" option from the UI and installs
	                   a security guard that reverts any attempt to select it.
	   ═══════════════════════════════════════════════════════════════════════ */

	/**
	 * Find the fieldset that owns the Pricing option by looking for one that
	 * contains BOTH a "Wholesale" and a "Retail" radio/option input.
	 * This is option-name–agnostic so it works even if the merchant renames
	 * the option from "Pricing" to something else.
	 */
	const findPricingFieldset = (
		variantPicker,
		wholesaleValue,
		retailValue
	) => {
		const fieldsets = variantPicker.querySelectorAll(
			'fieldset.product-form__controls'
		);
		const selWholesale = `input[value="${CSS.escape(wholesaleValue)}"]`;
		const selRetail = `input[value="${CSS.escape(retailValue)}"]`;
		for (const fieldset of fieldsets) {
			const hasWholesale = fieldset.querySelector(selWholesale);
			const hasRetail = fieldset.querySelector(selRetail);
			if (hasWholesale && hasRetail) return fieldset;
		}
		return null;
	};

	/** Liquid / Shopify may render a tier as unavailable; without this, .click() does nothing. */
	const enablePricingTierInputs = (pricingFieldset) => {
		pricingFieldset
			.querySelectorAll('input[type="radio"]')
			.forEach((input) => {
				input.disabled = false;
				input.removeAttribute('disabled');
				input.classList.remove('disabled');
			});
	};

	const variantPickerHasWholesale = (
		variantPicker,
		wholesaleValue
	) => {
		const cv = variantPicker?.currentVariant;
		if (!cv || !Array.isArray(cv.options)) return false;
		return cv.options.indexOf(wholesaleValue) !== -1;
	};

	/**
	 * Programmatically select a value in the Pricing fieldset.
	 * Works for both the button (radio) picker and the dropdown picker.
	 */
	const selectPricingOption = (variantPicker, pricingFieldset, value) => {
		const isDropdown = pricingFieldset.classList.contains(
			'product-form__controls--dropdown'
		);

		if (isDropdown) {
			const dropdownEl   = pricingFieldset.querySelector('variant-dropdown-select');
			const hiddenInput  = dropdownEl
				? dropdownEl.querySelector('input[type="hidden"]')
				: null;
			const targetRadio  = pricingFieldset.querySelector(
				`input[type="radio"][value="${CSS.escape(value)}"]`
			);

			if (!hiddenInput || !targetRadio) return;

			targetRadio.disabled = false;
			targetRadio.removeAttribute('disabled');
			targetRadio.classList.remove('disabled');

			// Update the displayed current-value text
			const currentValueEl = dropdownEl.querySelector(
				'[data-dropdown-current-value]'
			);
			if (currentValueEl) currentValueEl.textContent = value;

			// Sync hidden input
			hiddenInput.value = value;

			// Sync radio checked states so updateOptions() reads correctly
			pricingFieldset
				.querySelectorAll('input[type="radio"]')
				.forEach((r) => {
					r.checked = false;
					r.removeAttribute('checked');
				});
			targetRadio.checked = true;
			targetRadio.setAttribute('checked', 'true');

			// Trigger the theme's variant-change pipeline (always — fixes stale SSR price)
			hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
		} else {
			// Button / radio picker
			const targetRadio = pricingFieldset.querySelector(
				`input[type="radio"][value="${CSS.escape(value)}"]`
			);
			if (!targetRadio) return;

			targetRadio.disabled = false;
			targetRadio.removeAttribute('disabled');
			targetRadio.classList.remove('disabled');

			// Native click is most reliable across browsers for custom elements
			targetRadio.click();
		}
	};

	/**
	 * Hide a single radio option (and its label) from the UI without
	 * removing it from the DOM — the hidden input still goes to the cart.
	 */
	const hideOptionFromUI = (input) => {
		if (!input) return;

		input.classList.add('wholesale-option-hidden');
		input.setAttribute('aria-hidden', 'true');
		input.setAttribute('tabindex', '-1');

		// Paired <label> for button picker
		const label = input.id
			? document.querySelector(`label[for="${CSS.escape(input.id)}"]`)
			: null;
		if (label) {
			label.classList.add('wholesale-option-hidden');
			label.setAttribute('aria-hidden', 'true');
		}

		// Parent <li> for dropdown picker
		const listItem = input.closest('li');
		if (listItem) listItem.classList.add('wholesale-option-hidden');
	};

	/**
	 * Install a security guard that reverts any attempt by a non-wholesaler
	 * to select the "Wholesale" pricing option (e.g., via DevTools).
	 * Uses `requestAnimationFrame` so the revert fires AFTER the theme's own
	 * change handler has run, guaranteeing a clean state transition back to
	 * Retail pricing.
	 */
	const installSecurityGuard = (
		variantPicker,
		pricingFieldset,
		retailValue,
		wholesaleValue
	) => {
		variantPicker.addEventListener('change', () => {
			requestAnimationFrame(() => {
				const wholesaleInput = pricingFieldset.querySelector(
					`input[value="${CSS.escape(wholesaleValue)}"]`
				);
				if (wholesaleInput && wholesaleInput.checked) {
					// Revert silently to Retail
					selectPricingOption(variantPicker, pricingFieldset, retailValue);
				}
			});
		});
	};

	/**
	 * Main entry point — processes every `.wholesale-config` block on the page.
	 * Called on initial load AND whenever a theme section is dynamically reloaded.
	 */
	const initWholesalePricing = () => {
		document.querySelectorAll('.wholesale-config').forEach((configEl) => {
			// Skip if already initialised for this section
			if (configEl.dataset.wholesaleInit === 'true') return;
			configEl.dataset.wholesaleInit = 'true';

			let config;
			try {
				config = JSON.parse(configEl.textContent);
			} catch (e) {
				console.warn('[Wholesale] Could not parse config:', e);
				return;
			}

			const { isWholesale, sectionId, wholesaleValue, retailValue } = config;
			const sid = String(sectionId);

			/* Strict boolean — avoids truthy strings; only tagged customers get wholesale UI */
			const wholesaleCustomer =
				isWholesale === true || isWholesale === 'true';

			const badge = document.getElementById(`WholesaleBadge-${sid}`);
			if (badge && !wholesaleCustomer) {
				badge.hidden = true;
				badge.setAttribute('hidden', '');
			}

			// Locate the variant picker for this section (supports both types)
			const variantPicker = document.getElementById(`variant-picker-${sid}`);
			if (!variantPicker) return; // Product may have no variants

			// Locate the Pricing fieldset (uses values from theme / product config)
			const pricingFieldset = findPricingFieldset(
				variantPicker,
				wholesaleValue,
				retailValue
			);
			if (!pricingFieldset) return; // Product not configured with Pricing option

			const wholesaleInput = pricingFieldset.querySelector(
				`input[value="${CSS.escape(wholesaleValue)}"]`
			);
			const retailInput = pricingFieldset.querySelector(
				`input[value="${CSS.escape(retailValue)}"]`
			);
			if (!wholesaleInput || !retailInput) return;

			if (wholesaleCustomer) {
				/* ── WHOLESALER PATH ─────────────────────────────────────────── */
				const finishWholesaleUi = () => {
					pricingFieldset.classList.add('wholesale-pricing-hidden');
					if (badge) {
						badge.hidden = false;
						badge.removeAttribute('hidden');
						const infoCol = document.querySelector(
							`#ProductInfo-${sid} .product__info-container--col`
						);
						if (infoCol) infoCol.appendChild(badge);
					}
				};

				const attemptWholesaleSelection = () => {
					enablePricingTierInputs(pricingFieldset);
					selectPricingOption(variantPicker, pricingFieldset, wholesaleValue);
				};

				let tries = 0;
				const verifyAndFinalize = () => {
					if (variantPickerHasWholesale(variantPicker, wholesaleValue)) {
						finishWholesaleUi();
						return;
					}
					tries += 1;
					if (tries > 8) {
						if (badge) {
							badge.hidden = true;
							badge.setAttribute('hidden', '');
						}
						return;
					}
					attemptWholesaleSelection();
					setTimeout(verifyAndFinalize, tries === 1 ? 40 : 120);
				};

				verifyAndFinalize();
			} else {
				/* ── RETAIL / GUEST PATH ─────────────────────────────────────── */

				if (badge) {
					badge.hidden = true;
					badge.setAttribute('hidden', '');
				}

				// 1. Ensure Retail is the selected option
				enablePricingTierInputs(pricingFieldset);
				selectPricingOption(variantPicker, pricingFieldset, retailValue);

				// 2. Hide the Wholesale option from the UI
				hideOptionFromUI(wholesaleInput);

				// 3. Install tamper guard — reverts DevTools-based manipulation
				installSecurityGuard(
					variantPicker,
					pricingFieldset,
					retailValue,
					wholesaleValue
				);
			}
		});
	};

	const initProductAccordion = () => {
		const aboutToggleItems = $(".about__accordion-toggle");

		aboutToggleItems.each(function () {
			const currentToggle = $(this);
			const siblingToggles = currentToggle.siblings(
				".about__accordion-description"
			);

			currentToggle.click(function () {
				if (!currentToggle.hasClass("active")) {
					aboutToggleItems.each(function () {
						const siblingToggle = $(this);
						siblingToggle.removeClass("active");
						siblingToggle
							.siblings(".about__accordion-description")
							.stop()
							.slideUp(300);
					});

					currentToggle.addClass("active");

					siblingToggles.stop().slideDown(300);
				} else {
					currentToggle.removeClass("active");
					siblingToggles.stop().slideUp(300);
				}
			});
		});
	};

	const initZoomImage = () => {
		const imagesWrapper = document.querySelector(
			".product-media-modal__content"
		);
		const images = imagesWrapper?.querySelectorAll(".js-image-zoom") || [];

		images.forEach((el) => {
			el.addEventListener("click", (e) => {
				imagesWrapper.classList.toggle("zoom");
			});
		});
	};

	const formatFreeShippingAmount = (value) => {
		const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
		const formatString = theme.moneyFormat;

		return formatString.replace(placeholderRegex, value);
	};

	const setTotalFreeShipping = () => {
		if (document.querySelector(".js-free-shipping")) {
			const freeShippingTotal = document.querySelector(".free-shipping-total");
			if (freeShippingTotal) {
				const minSpend = Number(freeShippingTotal.dataset.minSpend);
				if (minSpend && minSpend > 0) {
					freeShippingTotal.innerText = `${formatFreeShippingAmount(
						Math.round(minSpend * (Shopify.currency.rate || 1))
					)}`;
				}
			}
		}
	};

	const revealStickyAddToCart = (section) => {
		if (!section && section.classList.contains(".product-section")) return;
		const buyButtons = section.querySelector(
			".product__buy-buttons > product-form"
		);
		const stickyBar = section.querySelector(".sticky-add-to-cart");
		const footerBottom = document.querySelector(
			".shopify-section-group-footer-group .footer__content-bottom"
		);

		if (!stickyBar) return;

		const observerOptions = {
			root: null,
			rootMargin: "0px",
			threshold: 0.01,
		};

		const isElementBelowScroll = (element) => {
			return element ? element.getBoundingClientRect().top > 0 : false;
		};

		const toggleStickyBar = (isVisible) => {
			if (isVisible || isElementBelowScroll(buyButtons)) {
				stickyBar.classList.remove("active");
			} else {
				stickyBar.classList.add("active");
			}
		};

		const handleIntersect = (entries) => {
			const isVisible = entries.some((entry) => entry.isIntersecting);
			toggleStickyBar(isVisible);
		};

		const observer = new IntersectionObserver(handleIntersect, observerOptions);

		if (buyButtons) observer.observe(buyButtons);
		if (footerBottom) observer.observe(footerBottom);
	};

	const setRadioValue = () => {
		document.querySelectorAll(".product-form__controls").forEach((fieldset) => {
			const legendSpan = fieldset.querySelector(".selected-value");
			if (!legendSpan) return;

			const radios = fieldset.querySelectorAll('input[type="radio"]');

			radios.forEach((radio) => {
				radio.addEventListener("change", function () {
					if (this.checked) {
						legendSpan.textContent = this.value;
					}
				});
			});
		});
	};

	const setTabsText = () => {
		const reviewsTab = document.getElementById("testimonials-tab");
		const allFeaturesTab = document.getElementById("all-features-tab");
		const reviewsSection = document.getElementById("testimonials-quote");
		const mediaSection = document.getElementById("media-section");
		const descriptionGridSection = document.getElementById(
			"description-grid-section"
		);
		const imageWithTextSection = document.getElementById(
			"image-with-text-section"
		);
		const simpleCardsSection = document.getElementById("simple-cards-section");
		const instructionsTab = document.getElementById("instructions-tab");
		const instructionsBlock = document.getElementById("instructions");

		if (!reviewsSection && reviewsTab) {
			reviewsTab.remove();
		}

		if (!instructionsBlock && instructionsTab) {
			instructionsTab.remove();
		}

		if (
			!mediaSection &&
			!descriptionGridSection &&
			!imageWithTextSection &&
			!simpleCardsSection &&
			allFeaturesTab
		) {
			allFeaturesTab.remove();
		}
	};

	function hideTabsIfSingle() {
		const track = document.querySelector(".tabs-lava-lamp");
		const navItems = document.querySelectorAll(
			".tabs-lava-lamp li:not(#tabs-lava-lamp-blob)"
		);

		if (track && navItems.length <= 1) {
			track.style.display = "none";
			return true;
		}
		return false;
	}

	const initStickyTabs = () => {
		const tabs = document.querySelector(".sticky-tabs");
		if (!tabs) return;

		const sentinel = document.createElement("div");
		sentinel.style.height = "1px";
		tabs.parentNode.insertBefore(sentinel, tabs);

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
						tabs.classList.add("is-fixed");
						tabs.classList.add("container");
					} else {
						tabs.classList.remove("is-fixed");
						tabs.classList.remove("container");
					}
				});
			},
			{ threshold: 0 }
		);

		observer.observe(sentinel);
	};

	const scheduleWholesaleInit = () => {
		requestAnimationFrame(() => {
			requestAnimationFrame(initWholesalePricing);
		});
	};

	document.addEventListener("shopify:section:load", function (event) {
		initProductAccordion();
		initZoomImage();
		setTotalFreeShipping();
		revealStickyAddToCart(event.target);
		initStickyTabs();
		setRadioValue();
		setTabsText();
		hideTabsIfSingle();
		scheduleWholesaleInit();
	});

	initProductAccordion();
	initZoomImage();
	setTotalFreeShipping();
	revealStickyAddToCart(document.currentScript.parentElement);
	initStickyTabs();
	setRadioValue();
	setTabsText();
	hideTabsIfSingle();
	scheduleWholesaleInit();
})();
